import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
// html2canvas is lazy-loaded on demand (see loadHtml2Canvas) to keep it out of the initial bundle.
import type html2canvas from 'html2canvas';
import { EncryptionService } from '../core/services/encryption.service';

interface Template {
  id: string;
  name: string;
  style: string;
}

@Component({
  selector: 'app-invitation-card-designer',
  templateUrl: './invitation-card-designer.component.html',
  styleUrls: ['./invitation-card-designer.component.css']
})
export class InvitationCardDesignerComponent implements OnInit {
  // Event details
  eventName: string = '';
  eventDate: string = '';
  eventLocation: string = '';
  eventTime: string = '';
  qrCodeImage: string = '';
  customMessage: string = 'You are cordially invited!';

  // Customization options
  selectedTemplate: string = 'template1';
  selectedFont: string = 'Playfair Display';
  titleFontSize: number = 28;
  bodyFontSize: number = 14;
  titleColor: string = '#333333';
  bodyColor: string = '#666666';
  backgroundColor: string = '#ffffff';
  accentColor: string = '#ff4c53';
  uploadedImage: string = '';

  // Page size options
  selectedPageSize: string = 'a4';
  pageSizes = [
    { id: 'a4', name: 'A4 (210 × 297mm)', aspectRatio: '210 / 297' },
    { id: 'a5', name: 'A5 (148 × 210mm)', aspectRatio: '148 / 210' },
    { id: 'letter', name: 'Letter (8.5 × 11in)', aspectRatio: '8.5 / 11' },
    { id: 'square', name: 'Square (1:1)', aspectRatio: '1 / 1' },
    { id: 'social', name: 'Social Media (1080 × 1080px)', aspectRatio: '1 / 1' },
    { id: 'story', name: 'Instagram Story (1080 × 1920px)', aspectRatio: '9 / 16' }
  ];

  // Available options
  templates: Template[] = [
    { id: 'template1', name: 'Classic', style: 'classic' },
    { id: 'template4', name: 'Fun', style: 'fun' },
    { id: 'custom', name: 'Custom', style: 'custom' }
  ];

  fonts: string[] = [
    'Playfair Display',
    '"Nunito", sans-serif',
    'Lato',
    '"Manrope", sans-serif',
    'Open Sans',
    '"Satisfy", cursive',
    'Raleway',
    'Crimson Text',
    '"Dancing Script", cursive',
    'Cormorant Garamond',
    'Georgia',
    'Arial',
    'Times New Roman',
    '"Pacifico", serif'
  ];

  isLoading: boolean = false;

  constructor(
    private dialogRef: MatDialogRef<InvitationCardDesignerComponent>,
    private encryptionService: EncryptionService
  ) {}

  ngOnInit(): void {
    // Load event details from localStorage
    this.eventName = localStorage.getItem('name') || 'Event Name';
    this.qrCodeImage = localStorage.getItem('web_qrImage') || '';
    
    // Load additional event details if available
    const eventData = localStorage.getItem('inviteData');
    if (eventData) {
      try {
        const decryptedData = JSON.parse(this.encryptionService.decryptData(eventData));
        // console.log(decryptedData); //__CONSOLE_LOG__

        if (decryptedData) {
          this.eventDate = this.formatDate(decryptedData.start_time) || '';
          this.eventTime = this.formatTime(decryptedData.start_time) || '';
          this.eventLocation = decryptedData.event_location || '';
          this.customMessage = decryptedData.message || 'You are cordially invited!';
        }
      } catch (error) {
        // console.error('Error decrypting invite data:', error); //__CONSOLE_LOG__
      }
    }
  }

  selectTemplate(templateId: string): void {
    this.selectedTemplate = templateId;
  }

  /** Lazily import html2canvas so it isn't part of the initial bundle. */
  private async loadHtml2Canvas(): Promise<typeof html2canvas> {
    const mod = await import('html2canvas');
    return mod.default;
  }

  async downloadCard(): Promise<void> {
    this.isLoading = true;
    const cardElement = document.getElementById('invitation-card');

    if (cardElement) {
      try {
        const html2canvas = await this.loadHtml2Canvas();
        const canvas = await html2canvas(cardElement, {
          scale: 2,
          backgroundColor: '#f5f5f5',
          logging: false,
          useCORS: true,
          allowTaint: true,
          scrollY: -window.scrollY,
          scrollX: -window.scrollX,
          windowWidth: cardElement.scrollWidth,
          windowHeight: cardElement.scrollHeight
        });
        
        // Create a new canvas with padding for shadow
        const padding = 60;
        const finalCanvas = document.createElement('canvas');
        finalCanvas.width = canvas.width + (padding * 2);
        finalCanvas.height = canvas.height + (padding * 2);
        const ctx = finalCanvas.getContext('2d');
        
        if (ctx) {
          // Fill background
          ctx.fillStyle = '#f5f5f5';
          ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
          
          // Draw the card with padding
          ctx.drawImage(canvas, padding, padding);
        }
        
        const link = document.createElement('a');
        link.download = `${this.eventName.replace(/\s+/g, '_')}_invitation.png`;
        link.href = finalCanvas.toDataURL('image/png');
        link.click();
        
        this.isLoading = false;
      } catch (error) {
        // console.error('Error generating image:', error); //__CONSOLE_LOG__
        this.isLoading = false;
      }
    }
  }

  async shareCard(): Promise<void> {
    this.isLoading = true;
    const cardElement = document.getElementById('invitation-card');
    
    if (cardElement) {
      try {
        const html2canvas = await this.loadHtml2Canvas();
        const canvas = await html2canvas(cardElement, {
          scale: 2,
          backgroundColor: null,
          logging: false
        });
        
        canvas.toBlob(async (blob) => {
          if (blob) {
            const file = new File([blob], `${this.eventName}_invitation.png`, { type: 'image/png' });
            
            if (navigator.share) {
              try {
                await navigator.share({
                  title: `${this.eventName} Invitation`,
                  text: 'You are invited!',
                  files: [file]
                });
              } catch (err) {
                // console.error('Share failed:', err); //__CONSOLE_LOG__
                // Fallback to download
                this.downloadCard();
              }
            } else {
              // Fallback to download if share is not supported
              this.downloadCard();
            }
          }
          this.isLoading = false;
        }, 'image/png');
      } catch (error) {
        // console.error('Error generating image:', error); //__CONSOLE_LOG__
        this.isLoading = false;
      }
    }
  }

  resetToDefaults(): void {
    this.selectedFont = 'Playfair Display';
    this.titleFontSize = 28;
    this.bodyFontSize = 14;
    this.titleColor = '#333333';
    this.bodyColor = '#666666';
    this.backgroundColor = '#ffffff';
    this.accentColor = '#ff4c53';
    this.customMessage = 'You are cordially invited!';
    this.uploadedImage = '';
  }

  onImageUpload(event: any): void {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.uploadedImage = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  removeImage(): void {
    this.uploadedImage = '';
  }

  close(): void {
    this.dialogRef.close();
  }

  getCardDimensions(): { aspectRatio: string } {
    const size = this.pageSizes.find(s => s.id === this.selectedPageSize);
    return {
      aspectRatio: size ? size.aspectRatio : '210 / 297'
    };
  }

  getCardStyles(): any {
    const dimensions = this.getCardDimensions();
    return {
      'font-family': this.selectedFont,
      'background-color': this.backgroundColor,
      'aspect-ratio': dimensions.aspectRatio
    };
  }

  getTitleStyles(): any {
    return {
      'font-size': `${this.titleFontSize}px`,
      'font-family': "'Manrope', sans-serif",
      'color': this.titleColor
    };
  }

  getBodyStyles(): any {
    return {
      'font-size': `${this.bodyFontSize}px`,
      'color': this.bodyColor
    };
  }

  getAccentStyles(): any {
    return {
      'color': this.accentColor
    };
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      const options: Intl.DateTimeFormatOptions = { 
        weekday: 'short', 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      };
      return date.toLocaleDateString('en-US', options);
    } catch (error) {
      // console.error('Error formatting date:', error); //__CONSOLE_LOG__
      return dateString;
    }
  }

  formatTime(dateString: string): string {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      const options: Intl.DateTimeFormatOptions = { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      };
      return date.toLocaleTimeString('en-US', options);
    } catch (error) {
      // console.error('Error formatting time:', error); //__CONSOLE_LOG__
      return dateString;
    }
  }
}
